export type ContentStatus = 'draft' | 'active' | 'archived'
export type EntryMode = 'start' | 'anchor'

interface StoryBase {
  story_id: string
  slug: string
  version: string
  title: string
  author_name?: string
  cover?: string
  status: ContentStatus
  unlock_title: string
  unlock_description: string
}

export interface StoryLock {
  id: string
  afterBlock: string
  characterCount: number
}

export interface StoryBlock {
  id: string
  html: string
  characterCount: number
}

export interface ReaderStoryVersion extends StoryBase {
  mode: 'reader'
  blocks: StoryBlock[]
  locks: StoryLock[]
}

export interface ChatParticipant {
  id: string
  name: string
  initials: string
  color?: string
}

interface ChatNodeBase {
  id: string
  delay_ms?: number
}

export type ChatIdleAction =
  | { id: string; type: 'typing'; after_ms: number; duration_ms: number }
  | { id: string; type: 'message'; after_ms: number; sender: string; text: string }
  | { id: string; type: 'hint'; after_ms: number; level: number; label: string; text: string }

export interface ChatMessageNode extends ChatNodeBase {
  type: 'message'
  sender: string
  text: string
  next?: string
}

export interface ChatSystemNode extends ChatNodeBase {
  type: 'system'
  text: string
  next?: string
}

export interface ChatVoiceNode extends ChatNodeBase {
  type: 'voice'
  sender: string
  transcript: string
  duration_seconds: number
  next?: string
}

export interface ChatActionNode extends ChatNodeBase {
  type: 'action'
  label: string
  next: string
}

export interface ChatImageNode extends ChatNodeBase {
  type: 'image'
  sender: string
  src: string
  alt: string
  caption?: string
  inspectable: boolean
  clues?: ChatImageClue[]
  min_clues?: number
  idle?: ChatIdleAction[]
  next?: string
}

export interface ChatImageClue {
  id: string
  x: number
  y: number
  radius: number
  label: string
  detail?: string
}

export interface ChatChoiceOption {
  id: string
  text: string
  next: string
  requires_clues?: string[]
}

export interface ChatChoiceNode extends ChatNodeBase {
  type: 'choice'
  prompt?: string
  options: ChatChoiceOption[]
  idle?: ChatIdleAction[]
}

export interface ChatCallNode extends ChatNodeBase {
  type: 'call'
  contact: string
  label?: string
  transcript?: string
  answered_transcript?: string
  declined_transcript?: string
  timeout_ms?: number
  idle?: ChatIdleAction[]
  next: string
}

export interface ChatPuzzleNode extends ChatNodeBase {
  type: 'puzzle'
  prompt: string
  placeholder?: string
  answer: string
  hint?: string
  success_text?: string
  idle?: ChatIdleAction[]
  next: string
}

export interface ChatLockNode extends ChatNodeBase {
  type: 'lock'
}

export type ChatNode = ChatMessageNode | ChatSystemNode | ChatVoiceNode | ChatActionNode | ChatImageNode | ChatChoiceNode | ChatCallNode | ChatPuzzleNode | ChatLockNode

export interface ChatStoryVersion extends StoryBase {
  mode: 'chat'
  subtitle?: string
  participants: ChatParticipant[]
  nodes: ChatNode[]
  start_node: string
}

export type StoryVersion = ReaderStoryVersion | ChatStoryVersion

interface LandingBase {
  landing_id: string
  story: string
  version: string
  entry_mode: EntryMode
  entry_context?: string
  lock: string
  creative: string
  app_handoff?: {
    target_landing: string
  }
  config_revision: number
  status: ContentStatus
}

export interface ReaderLanding extends LandingBase {
  start_block: string
  start_node?: never
}

export interface ChatLanding extends LandingBase {
  start_node: string
  preload_nodes?: string[]
  first_interactive_node?: string
  ad_last_node?: string
  start_block?: never
}

export type Landing = ReaderLanding | ChatLanding

export interface ContentRegistry {
  stories: StoryVersion[]
  landings: Landing[]
  generatedAt: string
}

interface ResolvedExperienceBase {
  story: StoryVersion
  entryMode: EntryMode
  entryPoint: string
  entryContext?: string
  lockPoint: string
  contentDistance: number
  landing?: Landing
  preview: boolean
}

export interface ResolvedReader extends ResolvedExperienceBase {
  story: ReaderStoryVersion
  lock: StoryLock
  html: string
  entryBlock: string
  lockBlock: string
}

export interface ResolvedChatStory extends ResolvedExperienceBase {
  story: ChatStoryVersion
  startNode: string
  lockNode: string
  preloadedNodes: ChatNode[]
  firstInteractiveNode: string
}

export type ResolvedExperience = ResolvedReader | ResolvedChatStory

export function isResolvedChatStory(experience: ResolvedExperience): experience is ResolvedChatStory {
  return experience.story.mode === 'chat'
}
